import { NotFoundPage } from '@lbcamden/lib-react';
import { FunctionComponent } from 'react';
import { useParams } from 'react-router';

import { useProperty, usePropertyDetails } from '~/client/hooks/storeHooks';
import { useTinybaseStore } from '~/client/store/tinybase/tinybaseStore';
import { getPropertyValidity } from '~/client/utils/properties';

import { LoadInPage } from '../../common/LoadInPage';
import { PageTitle } from '../../common/PageTitle';
import { InvalidPropertyMessage } from '../../shared/InvalidPropertyMessage';
import { PropertyHeader } from '../../shared/PageHeader';
import { SyncFailedMessage } from '../../shared/SyncFailedMessage';
import { ArrearsArrangements } from './ArrearsArrangements';
import { Cases } from './Cases';
import { HousingApplications } from './HousingApplications';
import { Notes } from './Notes';
import { People } from './People';
import { PropertyAttributes } from './PropertyAttributes';
import { ServiceRequests } from './ServiceRequests';
import { SupportNeedsNotice } from './SupportNeedsNotice';
import { Visits } from './Visits';

export const PropertyPage: FunctionComponent = () => {
  const { propertyId: propertyIdParam } = useParams();
  const propertyId = parseInt(propertyIdParam!);

  const { isSyncing, hasSyncError, syncTimedOut } = useTinybaseStore(
    (state) => state.propertyDataSyncState,
  );
  const property = useProperty(propertyId);
  const { noResidentsFound } = usePropertyDetails(propertyId) || {};

  if (!property) {
    return <NotFoundPage />;
  }

  const syncFailed = hasSyncError || syncTimedOut;
  const validity = getPropertyValidity(property, noResidentsFound);

  return (
    <LoadInPage loading={isSyncing}>
      <PageTitle title={property.address} />
      <PropertyHeader property={property} includeDetails />
      {validity.isValid ? (
        <>
          {syncFailed && <SyncFailedMessage />}
          {!syncFailed && (
            <>
              <SupportNeedsNotice propertyId={propertyId} />
              <PropertyAttributes propertyId={propertyId} />
              <People propertyId={propertyId} isTenants />
              <People propertyId={propertyId} />
              <ArrearsArrangements propertyId={propertyId} />
              <Cases propertyId={propertyId} />
              <ServiceRequests buildingId={property.buildingId} propertyId={property.propertyId} />
              <HousingApplications propertyId={propertyId} />
              <Notes propertyId={propertyId} />
              <Visits propertyId={propertyId} />
            </>
          )}
        </>
      ) : (
        <InvalidPropertyMessage invalidPropertyKind={validity.reason!} />
      )}
    </LoadInPage>
  );
};
